// Split out of src/components/SettingsTab.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// SettingsTab.jsx re-exports everything it exported before, so existing imports keep working.

import { apiGetDevices, getDeviceId, getDeviceOwnerAdultId, householdFetch } from "../../lib/api.js";
import { adultForDevice, attributionName } from "../../lib/adults.js";
import { fmtTimestamp, logSetting } from "../../lib/utils.js";
import { t as ct } from "../../lib/content.js";
import { publicPageHref } from "../../lib/origin.js";
import { exportFile } from "../../lib/share.js";
import { actor, AttentionDot } from "./shared.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------------- security ---------------------------------- */

function SecuritySection({ state, update, showToast, verifiedPin, setPendingVerifiedPin, code }) {
  // W2-PR(iii) — the "History" half of Security & History (review 3.6): read-only view
  // of the settings audit ring, newest first. Device labels resolved from the registry
  // once per visit; unknown ids (device since removed) fall back to a short id, and
  // "system" marks machine-initiated changes. This is dispute context, not compromise
  // detection (P5): in a one-device household every row says the same device — the
  // value is "when did the rate change and to what", not "who dunnit".
  const [deviceLabels, setDeviceLabels] = useState({});
  useEffect(() => {
    let alive = true;
    apiGetDevices(code).then((d) => {
      if (!alive) return;
      const map = {};
      for (const [id, rec] of Object.entries(d || {})) map[id] = rec.customLabel || rec.label || id.slice(0, 6);
      setDeviceLabels(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, [code]);
  const LOG_LABELS = {
    exchangeRate: ct("settings.log.what_rate", "Token value"),
    "vaultInterest.apr": ct("settings.log.what_apr", "Vault interest rate"),
    "vaultInterest.enabled": ct("settings.log.what_interest", "Vault interest"),
    parentPin: ct("settings.log.what_pin", "Parent PIN"),
    habitRewardMode: ct("settings.log.what_habit_mode", "Weekly reward"), // LB-13
    doubleTokenDay: ct("settings.log.what_double_day", "Double token day"), // LB-248
    adaptive: ct("settings.log.what_adaptive", "Learning Coach"), // LB-19
  };
  function viaLabel(id) {
    if (!id) return null;
    if (id === "system") return ct("settings.log.via_system", "automatic");
    return deviceLabels[id] || id.slice(0, 6);
  }
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");
  const [confirmingPin, setConfirmingPin] = useState(false);

  function savePin() {
    if (!/^\d{6}$/.test(newPin)) { setError("PIN must be exactly 6 digits."); return; }
    if (newPin !== confirmPin) { setError("PINs don't match."); return; }
    setError("");
    setConfirmingPin(true);
  }
  function confirmSavePin() {
    setPendingVerifiedPin(verifiedPin); // proves to the server we knew the current PIN
    // LB-340: change the PIN of the adult this device belongs to — on a shared device that
    // is the Owner (adultForDevice falls back), and on a claimed one it is the person
    // holding it, which is what "Change Parent PIN" means to them. The proof above is this
    // session's own verifiedPin, which is that same adult's current PIN, so the save path's
    // per-adult check passes exactly as the household-wide one used to.
    // No adult to write to means the PIN goes nowhere — so say so rather than reporting
    // success. The first cut set pinConfigured: true and toasted "Parent PIN updated"
    // regardless, which tells a parent their PIN changed when it did not (principle 5).
    // Checked HERE, not inside the updater — see PinSetupStep in Wizard.jsx for the full
    // reason. update() defers fn through setState, so a flag set inside it is still false
    // on the next line, and the first cut therefore reported failure on every PIN change
    // while the change itself went through.
    if (!adultForDevice(state, getDeviceOwnerAdultId(code))) {
      setError("Couldn't update the PIN just now. Check your connection and try again.");
      setConfirmingPin(false);
      return;
    }
    update((s) => {
      const adult = adultForDevice(s, getDeviceOwnerAdultId(code));
      if (!adult) return null; // belt and braces: no write, no revision bump, no autosave
      logSetting(s, getDeviceId(), "parentPin", "\u2022\u2022\u2022\u2022\u2022\u2022", "\u2022\u2022\u2022\u2022\u2022\u2022", actor(s));
      adult.pin = newPin;
      s.settings.pinConfigured = true; return s;
    });
    showToast(ct("toast.pin_updated", "Parent PIN updated"), "good");
    setNewPin(""); setConfirmPin(""); setConfirmingPin(false);
  }

  return (
    <>
    <div className="bg-white rounded-lg p-4 border border-slate-200">
      <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center">Change Parent PIN{!state.settings.pinConfigured && <AttentionDot />}</div>
      {!state.settings.pinConfigured && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3">
          Still on the starter PIN (123456) — set a real one so Parent Mode is actually locked.
        </div>
      )}
      {!confirmingPin ? (
        <>
          <input className="border border-slate-200 rounded-lg px-3 py-2 text-base w-full mb-2" placeholder="New 6-digit PIN" type="password" maxLength={6} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} />
          <input className="border border-slate-200 rounded-lg px-3 py-2 text-base w-full mb-2" placeholder="Confirm new PIN" type="password" maxLength={6} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} />
          {error && <div className="text-rose-600 text-base font-semibold mb-2">{error}</div>}
          <button className="w-full bg-slate-800 text-white font-bold text-sm rounded-xl py-2.5" onClick={savePin}>{ct("settings.save_pin", "Save PIN")}</button>
        </>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-base text-amber-700 font-semibold mb-2">Change the Parent Mode PIN to {newPin}?</p>
          <div className="flex gap-2">
            <button className="flex-1 bg-slate-800 text-white text-xs font-bold rounded-lg py-1.5" onClick={confirmSavePin}>{ct("settings.change_pin_confirm", "Yes, change PIN")}</button>
            <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setConfirmingPin(false)}>{ct("settings.cancel_button", "Cancel")}</button>
          </div>
        </div>
      )}
    </div>
      {(state.settingsLog || []).length > 0 && (
        <div className="bg-white rounded-lg p-4 border border-slate-200 mt-3">
          <div className="text-sm font-semibold text-slate-700 mb-2">{ct("settings.log.heading", "Change history")}</div>
          <p className="text-xs text-slate-400 mb-3">{ct("settings.log.sub", "Money and security settings changes, newest first — kept for the last 100 changes.")}</p>
          <div className="flex flex-col gap-2">
            {(state.settingsLog || []).slice(0, 20).map((e, i) => (
              <div key={`${e.at}-${i}`} className="border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                <div className="text-xs font-semibold text-slate-700">{LOG_LABELS[e.what] || e.what}: <span className="text-slate-400">{e.from}</span> → {e.to}</div>
                {/* LB-343 (G2): the settings log names the adult too — "Changed the exchange
                    rate / Jo · 10p to 12p a token" is a row on that screen. An older entry with
                    no adult on it keeps naming the device, which is all it ever knew. */}
                <div className="text-xs text-slate-400">{fmtTimestamp(e.at)}{attributionName(state, e) ? ` · ${attributionName(state, e)}` : viaLabel(e.deviceId) ? ` · via ${viaLabel(e.deviceId)}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------- data & privacy -------------------------------- */

function DangerZoneSection({ state, code, showToast, verifiedPin }) {
  const [typedConfirm, setTypedConfirm] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteSending, setDeleteSending] = useState(false);
  const [deleteSent, setDeleteSent] = useState(false);

  // Posts straight to the worker instead of handing off to a mailto: link — same reasoning
  // as FeedbackForm: mailto opens the OS mail app full-screen with no reliable way back
  // into the installed PWA. The worker relays it to closeaccount@littlebankers.uk.
  // W2-PR(iii): households with an owner email on file use the self-serve loop — the
  // request is PIN-proved here, and the destruction is authorised by clicking the link
  // that goes ONLY to the owner's inbox (see worker/handlers/deletion.js governance
  // note). Households with no owner email keep the old written-request relay.
  const selfServe = !!state.ownerEmail;
  async function requestDataDeletion() {
    setDeleteSending(true);
    try {
      // LB-179: through householdFetch so the device token rides along (both routes are gated).
      const res = await householdFetch(
        code,
        selfServe ? `/request-deletion` : `/delete-request`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // LB-182: the legacy relay is PIN-proved too now — same proof both ways.
          body: JSON.stringify(selfServe ? { verifiedPin } : { email: state.ownerEmail || "", verifiedPin }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setDeleteSent(true);
      setConfirmingDelete(false);
      showToast(ct("toast.deletion_request_sent", "Deletion request sent. We'll confirm once it's done"), "good");
    } catch (err) {
      showToast(err.message || ct("toast.deletion_send_failed", "Couldn't send. Check your connection and try again"), "bad");
    } finally {
      setDeleteSending(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white rounded-lg p-4 border border-slate-200">
        <div className="text-sm font-semibold text-slate-700 mb-2">{ct("settings.your_data_title", "Your data")}</div>
        {/* W5 — take-your-data-with-you (P7): both are generated ENTIRELY client-side
            from the state already on this device — no new server surface, nothing a
            stolen link could fetch. The JSON is the restore file for "Restore from a
            backup" on the create screen (which always makes a NEW household — P7). */}
        <div className="flex gap-2 mb-3">
          <button className="flex-1 bg-slate-100 text-slate-600 font-bold text-xs rounded-lg py-2" data-testid="export-csv" onClick={() => {
            const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
            const rows = [["timestamp", "child", "reason", "type", "balance", "amount", "status"].join(",")];
            for (const t of state.transactions || []) {
              if (t.type === "rate_marker") continue; // informational, not money
              const kid = (state.profiles || []).find((p) => p.id === t.childId);
              rows.push([esc(t.timestamp), esc(kid ? kid.name : ""), esc(t.reason), esc(t.type), esc(t.balance), esc(t.balance === "vault" ? (t.amount / 100).toFixed(2) : t.amount), esc(t.status || "")].join(","));
            }
            // Native Run C (LB-392): the web's download, or the app's share sheet on the file.
            exportFile({ name: `little-bankers-${code}-activity.csv`, mime: "text/csv", content: rows.join("\n") });
          }}>{ct("settings.export_csv", "Activity (CSV)")}</button>
          <button className="flex-1 bg-slate-100 text-slate-600 font-bold text-xs rounded-lg py-2" data-testid="export-backup" onClick={() => {
            const copy = JSON.parse(JSON.stringify(state));
            delete copy._meta; // revisions belong to the household that made them
            exportFile({ name: `little-bankers-${code}-backup.json`, mime: "application/json", content: JSON.stringify({ littleBankersBackup: 1, exportedAt: new Date().toISOString(), state: copy }, null, 2) });
          }}>{ct("settings.export_backup", "Full backup (JSON)")}</button>
        </div>
        <div className="flex flex-col gap-1.5">
          <a href={publicPageHref("/privacy")} className="text-sm font-bold text-slate-700 underline">Privacy Policy →</a>
          <a href={publicPageHref("/terms")} className="text-sm font-bold text-slate-700 underline">Terms & Conditions →</a>
        </div>
      </div>
      {/* LB-168: profile photos are retired — a one-line notice for 60 days from the deploy. */}
      {Date.now() < Date.parse("2026-11-07T00:00:00Z") && (
        <div className="text-xs text-slate-500 px-1" data-testid="photos-retired-notice">{ct("settings.photos_retired", "Profile photos have been retired; each child now has a character.")}</div>
      )}
      <div className="text-xs font-bold text-rose-600 px-1 -mb-1">{ct("settings.danger_zone_title", "Danger zone")}</div>
      <div className="bg-white rounded-lg p-4 border border-rose-200">
        <div className="text-sm font-bold text-rose-600 mb-1">{ct("settings.delete_household", "Delete household data")}</div>
        {deleteSent ? (
          <div className="rounded-xl bg-emerald-50 text-emerald-700 text-base font-semibold px-3 py-3 text-center">{selfServe ? ct("settings.delete.email_sent", "Check the owner's inbox — nothing is deleted until the link in that email is clicked. The link lasts 30 minutes.") : `✅ Request sent — we'll confirm once household ${code} is deleted.`}</div>
        ) : !confirmingDelete ? (
          <>
            <p className="text-base text-slate-500 mb-2">Permanently delete everything for household <b>{code}</b> — profiles, balances, goals, requests and history — for every parent and child. This can't be undone.</p>
            <button className="w-full bg-white border border-rose-300 text-rose-600 font-bold text-sm rounded-xl py-2.5" onClick={() => setConfirmingDelete(true)}>{ct("settings.request_account_deletion", "Request account deletion")}</button>
          </>
        ) : (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
            <p className="text-base text-rose-700 font-semibold mb-2">{selfServe ? ct("settings.delete.confirm_body", "This emails a confirmation link to {email}. Clicking that link permanently deletes household {code} — every balance, transaction, goal, photo and device link. Type DELETE below to send it.", { email: state.ownerEmail, code }) : `This sends a request to close household ${code} and delete all its data to closeaccount@littlebankers.uk. Nothing is deleted until we action it, and we'll confirm once it's done.`}</p>
            {selfServe && (
              <input data-testid="delete-typed-confirm" className="border border-rose-200 rounded-lg px-3 py-2 text-sm w-full mb-2 text-center tracking-widest" placeholder="DELETE" autoComplete="off" value={typedConfirm} onChange={(e) => setTypedConfirm(e.target.value)} />
            )}
            <div className="flex gap-2">
              <button disabled={deleteSending || (selfServe && typedConfirm.trim().toUpperCase() !== "DELETE")} className="flex-1 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-lg py-1.5 disabled:opacity-50" onClick={requestDataDeletion}>{deleteSending ? "Sending…" : selfServe ? ct("settings.delete.cta", "Email the confirmation link") : "Yes, request deletion"}</button>
              <button disabled={deleteSending} className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5 disabled:opacity-50" onClick={() => setConfirmingDelete(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { SecuritySection, DangerZoneSection };
